import {
  SphereGeometry,
  Mesh,
  MeshBasicMaterial,
  TubeGeometry,
  CatmullRomCurve3,
  BufferGeometry,
  Line,
  LineBasicMaterial,
  LineCurve3,
  BufferAttribute,
  Vector3,
  CylinderGeometry,
  Matrix4,
  Object3D,
} from "three";
import { CommonCommand } from "../command/commonCommand";
export class CurveUtil {
  constructor(scene, manager) {
    this.scene = scene;
    this.manager = manager;

    this.type = "curve"; // 直线或曲线 "line"/"curve"

    this.points = [];
    this.worldPos = [];

    this.enabled = true; // 是否允许添加点

    this._lineRadius = 0.1; // 线半径
    this._lineSegments = 8; // 线分段数
    this._sphereRadius = 0.2; // 控制点半径

    this.lineMat = new LineBasicMaterial({
      color: 0x306297,
    });
    this.tubeMat = new MeshBasicMaterial({
      color: 0x306297,
    });
  }

  /**
   * 曲线编辑中光标移动时触发
   * @param {*} point
   * @returns
   */
  onPointerMove(point) {
    if (!this.enabled) return;

    if (!this.currentPoint) {
      this.currentPoint = this._createSphere(point);
    } else {
      this.currentPoint.position.copy(point);
    }

    if (this.points.length > 0) {
      const points = [].concat(this.worldPos);
      points.push(point);
      this._updateLine(points, true);
    }
  }

  addPoint(worldPos) {
    if (!this.enabled) return;
    let mesh = this.currentPoint
      ? this.currentPoint
      : this._createSphere(worldPos);
    this.currentPoint = null;
    this.points.push(mesh);
    mesh.position.copy(worldPos);
    this.worldPos.push(worldPos);

    if (this.points.length > 1) {
      this._updateLine(this.worldPos);
    }

    // 历史记录
    {
      const state = {
        pos: worldPos.clone(),
        mesh: mesh,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          this.points.splice(this.points.length - 1, 1);
          this.worldPos.splice(this.worldPos.length - 1, 1);
          this.scene.remove(state.mesh);

          if (this.points.length > 1) {
            this._updateLine(this.worldPos);
          } else {
            this._clearLine();
          }
        },
        (state) => {
          this.points.push(state.mesh);
          this.worldPos.push(state.pos);
          state.mesh.position.copy(state.pos);

          if (this.points.length > 1) {
            this._updateLine(this.worldPos);
          } else {
            this._clearLine();
          }
        }
      );
      this.manager.addHistory(cmd);
    }
  }

  /**
   * 更新当前曲线，有两种模式，一种是正常按下鼠标左键添加控制点，一种是鼠标移动过程中实时更新（使用Line不然太耗性能）
   * @param {*} points
   * @param {*} isUpdate 是否是光标移动过程中
   */
  _updateLine(points, isUpdate = false) {
    this._clearLine();

    if (points.length < 2) return;

    switch (this.type) {
      case "line":
        if (!isUpdate) this._createLineTube(points);
        else {
          const geometry = new BufferGeometry();
          const vertex = [];
          for (var i = 0; i < points.length; i++) {
            vertex.push(points[i].x, points[i].y, points[i].z);
          }
          const vertices = new Float32Array(vertex);
          geometry.setAttribute("position", new BufferAttribute(vertices, 3));

          this.line = new Line(geometry, this.lineMat);
          this.scene.add(this.line);
        }
        break;
      case "curve":
        if (points.length === 2) {
          this.curve = new LineCurve3(points[0], points[1]);
        } else
          this.curve = new CatmullRomCurve3(points, false, "catmullrom", 0.5);
        const segment = Math.floor(this.curve.getLength() * 2);
        if (!isUpdate) {
          const tubeGeometry = new TubeGeometry(
            this.curve,
            segment,
            this._lineRadius,
            this._lineSegments,
            false
          );
          this.line = new Mesh(tubeGeometry, this.tubeMat);
        } else {
          if (points.length === 2) {
            const geometry = new BufferGeometry().setFromPoints(points);
            this.line = new Line(geometry, this.lineMat);
          } else {
            const linePoints = this.curve.getPoints(segment);

            const geometry = new BufferGeometry().setFromPoints(linePoints);
            this.line = new Line(geometry, this.lineMat);
          }
        }
        this.scene.add(this.line);
        break;
    }
  }

  _createSphere(pos) {
    if (!this.geo) {
      this.geo = new SphereGeometry(this._sphereRadius, 24, 24);
      this.mat = new MeshBasicMaterial({
        color: 0x06b0e4,
      });
    }

    const mesh = new Mesh(this.geo, this.mat);
    mesh.position.copy(pos);
    this.scene.add(mesh);
    return mesh;
  }

  /**
   * 在多个点之间创建多段圆柱
   * @param {*} points
   */
  _createLineTube(points) {
    this.line = new Object3D();
    for (var i = 0; i < points.length - 1; i++) {
      const cylinder = this._createCylinder(points[i], points[i + 1]);
      this.line.add(cylinder);
    }
    this.scene.add(this.line);
  }

  /**
   * 根据两点生成一个圆柱体
   * @param {*} pointA
   * @param {*} pointB
   * @returns
   */
  _createCylinder(pointA, pointB) {
    // 计算中点、高度和方向
    const height = pointA.distanceTo(pointB);
    const center = new Vector3().addVectors(pointA, pointB).multiplyScalar(0.5);

    // 创建圆柱体
    const geometry = new CylinderGeometry(
      this._lineRadius,
      this._lineRadius,
      height,
      this._lineSegments
    );
    const cylinder = new Mesh(geometry, this.tubeMat);

    // 定位和旋转
    cylinder.position.copy(center);

    cylinder.lookAt(pointB);
    // 修正旋转角度（默认的 Y 轴方向可能需要额外旋转）
    cylinder.rotateX(Math.PI / 2); // 若方向仍偏差，调整此处的旋转量

    return cylinder;
  }

  /**
   * 清除所有点和线
   */
  clear() {
    this.points.forEach((item) => {
      this.scene.remove(item);
    });
    this._clearLine();

    if (this.currentPoint) {
      this.scene.remove(this.currentPoint);
      this.currentPoint = null;
    }

    this.points = [];
    this.worldPos = [];
    this.enabled = true;
  }

  _clearLine() {
    if (this.line) {
      if (this.line.isMesh) {
        this.line.geometry.dispose();
      } else {
        this.line.traverse((node) => {
          if (node.geometry) node.geometry.dispose();
        });
      }
      this.line.removeFromParent();
      this.line = null;
    }
  }

  setType(type) {
    // console.log("setType", type);
    if (this.type === type) return;
    if (type === "curve") this.type = type;
    else this.type = "line";

    this.worldPos = [];
    for (var i = 0; i < this.points.length; i++) {
      this.worldPos.push(this.points[i].position.clone());
    }
    this._updateLine(this.worldPos);
  }

  /**
   * 停止绘制
   */
  stopDraw() {
    this.enabled = false;
    if (this.currentPoint) {
      this.scene.remove(this.currentPoint);
      this.currentPoint = null;
    }
    if (this.points.length > 1) {
      this.worldPos = [];
      for (var i = 0; i < this.points.length; i++) {
        this.worldPos.push(this.points[i].position.clone());
      }

      this._updateLine(this.worldPos);
    }
  }

  /**
   * 在曲线上获取等距点集
   * @param {*} curve
   * @param {*} distance
   * @returns
   */
  _getCurvePointsAtDistance(curve, distance) {
    const points = [];
    const totalLength = curve.getLength();
    let currentLength = 0;

    // 提高弧长计算精度（推荐）
    curve.arcLengthDivisions = 100;

    while (currentLength < totalLength) {
      // 注意条件改为 < 而非 <=
      const u = currentLength / totalLength;
      const t = curve.getUtoTmapping(u);

      const point = curve.getPoint(t);
      points.push(point);
      currentLength += distance;
    }

    return points;
  }

  _getLinePointsAtDistance(points, distance) {
    if (!points || points.length <= 0) return [];
    const res = [points[0]];
    let sum = 0;
    for (var i = 0; i < points.length - 1; i++) {
      let length = points[i].distanceTo(points[i + 1]);
      sum += length;
      while (sum >= distance) {
        let ratio = (sum - distance) / length;
        let point = new Vector3().lerpVectors(
          points[i],
          points[i + 1],
          1 - ratio
        );
        res.push(point);

        sum = sum - distance;
      }
    }
    return res;
  }

  /**
   * 获取绘制路径上等距点集
   * @param {*} dis
   */
  getPoints(dis) {
    if (this.type === "line") {
      return this._getLinePointsAtDistance(this.worldPos, dis);
    } else {
      if (!this.curve) return [];
      return this._getCurvePointsAtDistance(this.curve, dis);
    }
  }
}
